'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

type TimeRange = '7d' | '30d' | '90d';

const bookingTrendData: Record<TimeRange, Array<{ day: string; bookings: number; leads: number; revenue: number }>> = {
  '7d': [
    { day: 'Mon', bookings: 42, leads: 128, revenue: 9800 },
    { day: 'Tue', bookings: 38, leads: 115, revenue: 8600 },
    { day: 'Wed', bookings: 55, leads: 162, revenue: 12400 },
    { day: 'Thu', bookings: 61, leads: 178, revenue: 14200 },
    { day: 'Fri', bookings: 73, leads: 210, revenue: 17100 },
    { day: 'Sat', bookings: 89, leads: 245, revenue: 20800 },
    { day: 'Sun', bookings: 67, leads: 195, revenue: 15600 },
  ],
  '30d': [
    { day: 'Week 1', bookings: 312, leads: 890, revenue: 72400 },
    { day: 'Week 2', bookings: 287, leads: 820, revenue: 66200 },
    { day: 'Week 3', bookings: 345, leads: 980, revenue: 79800 },
    { day: 'Week 4', bookings: 303, leads: 870, revenue: 70100 },
  ],
  '90d': [
    { day: 'Jan', bookings: 1120, leads: 3200, revenue: 258000 },
    { day: 'Feb', bookings: 980, leads: 2800, revenue: 226000 },
    { day: 'Mar', bookings: 1247, leads: 3560, revenue: 284500 },
  ],
};

const conversionFunnelData = [
  { stage: 'Visitors', count: 45200, color: '#0066CC' },
  { stage: 'Searches', count: 18400, color: '#0052A3' },
  { stage: 'Leads', count: 3560, color: '#FF6B35' },
  { stage: 'Bookings', count: 1247, color: '#10B981' },
];

const popularRoutesData = [
  { route: 'LHR \u2192 DXB', bookings: 342, revenue: 78400, growth: 18.5, avgPrice: 229 },
  { route: 'MAN \u2192 JFK', bookings: 287, revenue: 65200, growth: 12.3, avgPrice: 227 },
  { route: 'LHR \u2192 SIN', bookings: 234, revenue: 52800, growth: 9.8, avgPrice: 226 },
  { route: 'BHX \u2192 CDG', bookings: 198, revenue: 28600, growth: 22.1, avgPrice: 144 },
  { route: 'EDI \u2192 AMS', bookings: 156, revenue: 22100, growth: 15.7, avgPrice: 142 },
  { route: 'LHR \u2192 BKK', bookings: 143, revenue: 38600, growth: 8.4, avgPrice: 270 },
  { route: 'MAN \u2192 DXB', bookings: 128, revenue: 29400, growth: 31.2, avgPrice: 230 },
  { route: 'LGW \u2192 BCN', bookings: 112, revenue: 14600, growth: 19.6, avgPrice: 130 },
];

const userActivityData = [
  { hour: '00', active: 45 },
  { hour: '02', active: 28 },
  { hour: '04', active: 18 },
  { hour: '06', active: 62 },
  { hour: '08', active: 145 },
  { hour: '10', active: 312 },
  { hour: '12', active: 428 },
  { hour: '14', active: 398 },
  { hour: '16', active: 445 },
  { hour: '18', active: 512 },
  { hour: '20', active: 478 },
  { hour: '22', active: 234 },
];

const leadSourceData = [
  { name: 'Organic Search', value: 38, color: '#0066CC' },
  { name: 'Direct', value: 24, color: '#10B981' },
  { name: 'Social Media', value: 18, color: '#FF6B35' },
  { name: 'Referral', value: 12, color: '#8B5CF6' },
  { name: 'Paid Ads', value: 8, color: '#F59E0B' },
];

const revenueByServiceData = [
  { service: 'Flights', revenue: 184500, bookings: 842 },
  { service: 'Hotels', revenue: 52400, bookings: 234 },
  { service: 'Holidays', revenue: 38600, bookings: 98 },
  { service: 'Onward Tickets', revenue: 9000, bookings: 73 },
];

const AnalyticsDashboard = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [activeMetric, setActiveMetric] = useState<'bookings' | 'leads' | 'revenue'>('bookings');
  const [liveStats, setLiveStats] = useState({
    onlineUsers: 312,
    todayBookings: 47,
    todayRevenue: 10840,
    todayLeads: 143,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveStats((prev) => ({
        onlineUsers: Math.max(50, prev.onlineUsers + Math.floor(Math.random() * 10 - 4)),
        todayBookings: prev.todayBookings + (Math.random() > 0.7 ? 1 : 0),
        todayRevenue: prev.todayRevenue + (Math.random() > 0.7 ? Math.floor(Math.random() * 500) : 0),
        todayLeads: prev.todayLeads + (Math.random() > 0.6 ? 1 : 0),
      }));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const kpiCards = [
    { label: 'Total Bookings', value: timeRange === '7d' ? '425' : timeRange === '30d' ? '1,247' : '3,347', change: '+12.5%', icon: 'TicketIcon', bg: 'bg-blue-50', text: 'text-blue-600' },
    { label: 'Revenue (GBP)', value: timeRange === '7d' ? '\u00a398,500' : timeRange === '30d' ? '\u00a3284,500' : '\u00a3768,500', change: '+18.2%', icon: 'CurrencyPoundIcon', bg: 'bg-green-50', text: 'text-green-600' },
    { label: 'Total Leads', value: timeRange === '7d' ? '1,233' : timeRange === '30d' ? '3,560' : '9,800', change: '+24.7%', icon: 'UserPlusIcon', bg: 'bg-orange-50', text: 'text-orange-600' },
    { label: 'Conversion Rate', value: '3.8%', change: '+0.4%', icon: 'ArrowTrendingUpIcon', bg: 'bg-purple-50', text: 'text-purple-600' },
    { label: 'Avg Booking Value', value: '\u00a3228', change: '+5.1%', icon: 'BanknotesIcon', bg: 'bg-teal-50', text: 'text-teal-600' },
    { label: 'Active Users', value: timeRange === '7d' ? '4,832' : timeRange === '30d' ? '8,432' : '22,100', change: '+7.8%', icon: 'UserGroupIcon', bg: 'bg-indigo-50', text: 'text-indigo-600' },
  ];

  const trendData = bookingTrendData[timeRange];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-black text-foreground">Analytics Dashboard</h2>
          <p className="text-sm text-muted-foreground mt-1">Real-time business intelligence &amp; performance metrics</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs font-bold text-green-700">{liveStats.onlineUsers} online now</span>
          </div>
          <div className="flex gap-1">
            {(['7d', '30d', '90d'] as TimeRange[]).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all duration-200 ${
                  timeRange === range ? 'bg-primary text-white' : 'bg-surface text-muted-foreground hover:bg-muted'
                }`}
              >
                {range === '7d' ? '7D' : range === '30d' ? '30D' : '90D'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Live Today Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Today's Bookings", value: liveStats.todayBookings, icon: 'TicketIcon', color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: "Today's Revenue", value: `\u00a3${liveStats.todayRevenue.toLocaleString()}`, icon: 'CurrencyPoundIcon', color: 'text-green-600', bg: 'bg-green-50' },
          { label: "Today's Leads", value: liveStats.todayLeads, icon: 'UserPlusIcon', color: 'text-orange-600', bg: 'bg-orange-50' },
          { label: 'Users Online', value: liveStats.onlineUsers, icon: 'SignalIcon', color: 'text-purple-600', bg: 'bg-purple-50' },
        ].map((stat, i) => (
          <div key={i} className={`${stat.bg} rounded-xl p-3 border border-border`}>
            <div className="flex items-center gap-2 mb-1">
              <Icon name={stat.icon as any} size={16} className={stat.color} />
              <span className="text-xs text-muted-foreground">{stat.label}</span>
            </div>
            <div className={`text-xl font-black ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpiCards.map((card, index) => (
          <div key={index} className="bg-white rounded-xl p-4 border border-border hover:shadow-md transition-all duration-300">
            <div className={`w-10 h-10 ${card.bg} rounded-lg flex items-center justify-center mb-3`}>
              <Icon name={card.icon as any} size={20} className={card.text} />
            </div>
            <div className="text-xl font-black text-foreground leading-tight">{card.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5 mb-1">{card.label}</div>
            <div className="flex items-center gap-1 text-green-600">
              <Icon name="ArrowTrendingUpIcon" size={12} />
              <span className="text-xs font-bold">{card.change}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Trend Chart */}
      <div className="bg-white rounded-xl border border-border p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <h3 className="text-lg font-heading font-black text-foreground">Bookings, Leads &amp; Revenue Trend</h3>
          <div className="flex gap-2">
            {(['bookings', 'leads', 'revenue'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setActiveMetric(m)}
                className={`px-3 py-1 rounded-lg text-xs font-bold capitalize transition-all ${
                  activeMetric === m ? 'bg-primary text-white' : 'bg-surface text-muted-foreground hover:bg-muted'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0066CC" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#0066CC" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(value: number, name: string) => [
                name === 'revenue' ? `\u00a3${value.toLocaleString()}` : value,
                name.charAt(0).toUpperCase() + name.slice(1),
              ]}
            />
            <Legend />
            {activeMetric !== 'revenue' && (
              <Area type="monotone" dataKey="bookings" stroke="#0066CC" fill="url(#colorBookings)" strokeWidth={2} name="Bookings" />
            )}
            {activeMetric === 'leads' && (
              <Area type="monotone" dataKey="leads" stroke="#FF6B35" fill="url(#colorLeads)" strokeWidth={2} name="Leads" />
            )}
            {activeMetric === 'revenue' && (
              <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="url(#colorRevenue)" strokeWidth={2} name="Revenue" />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Conversion Funnel + Lead Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-border p-5">
          <h3 className="text-lg font-heading font-black text-foreground mb-4">Conversion Funnel</h3>
          <div className="space-y-3">
            {conversionFunnelData.map((stage, index) => {
              const pct = Math.round((stage.count / conversionFunnelData[0].count) * 100);
              const convRate = index > 0
                ? `${((stage.count / conversionFunnelData[index - 1].count) * 100).toFixed(1)}% from prev`
                : '100% baseline';
              return (
                <div key={index}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-bold text-foreground">{stage.stage}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">{convRate}</span>
                      <span className="text-sm font-black text-foreground">{stage.count.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="h-8 bg-gray-100 rounded-lg overflow-hidden">
                    <div
                      className="h-full rounded-lg flex items-center px-3 transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: stage.color }}
                    >
                      <span className="text-white text-xs font-bold">{pct}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2">
              <Icon name="ArrowTrendingUpIcon" size={16} className="text-green-600" />
              <span className="text-sm font-bold text-green-700">Overall Conversion: 2.76% (Visitors &#8594; Bookings)</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border p-5">
          <h3 className="text-lg font-heading font-black text-foreground mb-4">Lead Sources</h3>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="50%" height={180}>
              <PieChart>
                <Pie data={leadSourceData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                  {leadSourceData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, 'Share']} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {leadSourceData.map((source, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: source.color }} />
                    <span className="text-xs text-muted-foreground">{source.name}</span>
                  </div>
                  <span className="text-xs font-bold text-foreground">{source.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Popular Routes Table */}
      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="p-5 border-b border-border">
          <h3 className="text-lg font-heading font-black text-foreground">Popular Routes Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">Route</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">Bookings</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">Revenue</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">Avg Price</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">Growth</th>
              </tr>
            </thead>
            <tbody>
              {popularRoutesData.map((route, index) => (
                <tr key={index} className="border-t border-border hover:bg-surface transition-colors">
                  <td className="px-4 py-3 font-bold text-foreground text-sm">{route.route}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${(route.bookings / 342) * 100}%` }} />
                      </div>
                      <span className="text-sm text-muted-foreground">{route.bookings}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-bold text-green-600 text-sm">\u00a3{route.revenue.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">\u00a3{route.avgPrice}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                      route.growth > 20 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      <Icon name="ArrowTrendingUpIcon" size={10} />
                      +{route.growth}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Revenue by Service + User Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-border p-5">
          <h3 className="text-lg font-heading font-black text-foreground mb-4">Revenue by Service</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={revenueByServiceData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="service" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `\u00a3${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value: number) => [`\u00a3${value.toLocaleString()}`, 'Revenue']} />
              <Bar dataKey="revenue" fill="#0066CC" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-border p-5">
          <h3 className="text-lg font-heading font-black text-foreground mb-4">User Activity by Hour</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={userActivityData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}:00`} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={(v) => `${v}:00`} formatter={(value: number) => [value, 'Active Users']} />
              <Line type="monotone" dataKey="active" stroke="#FF6B35" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-muted-foreground mt-2">Peak activity: 18:00 &#8212; 20:00 (512 users)</p>
        </div>
      </div>

      {/* AI Performance */}
      <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl border border-primary/20 p-5">
        <h3 className="text-lg font-heading font-black text-foreground mb-4">AI Performance Metrics</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'AI Recommendations', value: '12,847', sub: '94% accuracy', icon: 'SparklesIcon', color: 'text-primary' },
            { label: 'Chat Interactions', value: '5,234', sub: '98.5% satisfaction', icon: 'ChatBubbleLeftRightIcon', color: 'text-green-600' },
            { label: 'Price Alerts Sent', value: '3,421', sub: '42% conversion', icon: 'BellAlertIcon', color: 'text-orange-600' },
            { label: 'Visa Analyses', value: '2,156', sub: '99% accuracy', icon: 'DocumentMagnifyingGlassIcon', color: 'text-purple-600' },
          ].map((m, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border border-border">
              <Icon name={m.icon as any} size={24} className={`${m.color} mb-2`} />
              <div className="text-2xl font-black text-foreground">{m.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{m.label}</div>
              <div className="text-xs font-bold text-green-600 mt-1">{m.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Export Actions */}
      <div className="flex flex-wrap gap-3">
        <button className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white font-bold rounded-lg hover:bg-secondary transition-all duration-200 text-sm">
          <Icon name="ArrowDownTrayIcon" size={18} />
          <span>Export PDF Report</span>
        </button>
        <button className="flex items-center gap-2 px-5 py-2.5 bg-surface text-foreground font-bold rounded-lg border border-border hover:bg-muted transition-all duration-200 text-sm">
          <Icon name="TableCellsIcon" size={18} />
          <span>Export CSV Data</span>
        </button>
        <button className="flex items-center gap-2 px-5 py-2.5 bg-surface text-foreground font-bold rounded-lg border border-border hover:bg-muted transition-all duration-200 text-sm">
          <Icon name="ShareIcon" size={18} />
          <span>Share Dashboard</span>
        </button>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;